import { useState, useEffect, useMemo } from 'react';
import { useQuery } from '@tanstack/react-query';
import { OrderingFilters, type OrderReadinessFilter } from './components/OrderingFilters';
import { ManufacturerGroup } from './components/ManufacturerGroup';
import { useManufacturerGroups } from './hooks/useOrdering';
import { ActiveFiltersSummary } from '@/shared/components/ui/ActiveFiltersSummary';
import { supabase } from '@/shared/services/supabase';
import type { FrameFilters, Location, Manufacturer } from '@/shared/types/domain.types';

export function WeeklyOrderingPage() {
  const [filters, setFilters] = useState<FrameFilters>(() => {
    // Load location filter from localStorage if available
    const storedLocationIds = localStorage.getItem('selectedLocationIds');
    const locationIds = storedLocationIds ? JSON.parse(storedLocationIds) : undefined;

    return {
      ...(locationIds && locationIds.length > 0 ? { location_ids: locationIds } : {}),
    };
  });
  const [selectedFrameIds, setSelectedFrameIds] = useState<string[]>([]);
  const [orderReadiness, setOrderReadiness] = useState<OrderReadinessFilter>('ALL');
  const [showFilters, setShowFilters] = useState(() => {
    const stored = localStorage.getItem('weeklyOrderingFiltersVisible');
    return stored === null ? false : stored === 'true';
  });

  const { data: manufacturerGroups, isLoading, error, refetch } = useManufacturerGroups(filters);

  // Fetch locations
  const { data: locations } = useQuery({
    queryKey: ['locations'],
    queryFn: async () => {
      const { data, error } = await supabase
        .from('locations')
        .select('*')
        .eq('is_active', true)
        .order('name');
      if (error) throw error;
      return data as Location[];
    },
  });

  // Fetch manufacturers
  const { data: manufacturers } = useQuery({
    queryKey: ['manufacturers'],
    queryFn: async () => {
      const { data, error } = await supabase
        .from('manufacturers')
        .select('*')
        .eq('is_active', true)
        .order('name');
      if (error) throw error;
      return data as Manufacturer[];
    },
  });

  // Filter manufacturer groups based on order readiness
  const filteredManufacturerGroups = useMemo(() => {
    if (!manufacturerGroups || orderReadiness === 'ALL') {
      return manufacturerGroups;
    }

    return manufacturerGroups.filter((group) => {
      const hasMinOrderQty =
        group.manufacturer.min_order_qty !== null && group.manufacturer.min_order_qty > 0;

      if (!hasMinOrderQty) {
        // If no minimum order quantity, always show in "READY"
        return orderReadiness === 'READY';
      }

      const meetsMinimum = group.frameCount >= group.manufacturer.min_order_qty!;

      return orderReadiness === 'READY' ? meetsMinimum : !meetsMinimum;
    });
  }, [manufacturerGroups, orderReadiness]);

  // Build filter summary
  const filterSummary = useMemo(() => {
    const selectedLocations: { id: string; name: string }[] = [];
    const otherFilters: string[] = [];

    // Location filters (most important)
    if (filters.location_ids && filters.location_ids.length > 0) {
      filters.location_ids.forEach((id) => {
        const location = locations?.find((l) => l.id === id);
        if (location) selectedLocations.push({ id: location.id, name: location.name });
      });
    } else if (locations && locations.length > 0) {
      // Show all accessible locations if no filter is set
      selectedLocations.push(...locations.map((l) => ({ id: l.id, name: l.name })));
    }

    // Manufacturer filter
    if (filters.manufacturer_ids && filters.manufacturer_ids.length > 0) {
      const manufacturerNames = filters.manufacturer_ids
        .map((id) => manufacturers?.find((m) => m.id === id)?.name)
        .filter(Boolean);
      if (manufacturerNames.length > 0) {
        otherFilters.push(
          `Manufacturers: ${manufacturerNames.join(', ')}`
        );
      }
    }

    // Order type filter
    if (filters.order_type) {
      otherFilters.push(`Type: ${filters.order_type}`);
    }

    // Date range filter
    if (filters.date_from || filters.date_to) {
      const dateRange = [];
      if (filters.date_from) dateRange.push(`from ${filters.date_from}`);
      if (filters.date_to) dateRange.push(`to ${filters.date_to}`);
      otherFilters.push(`Date range: ${dateRange.join(' ')}`);
    }

    // Order readiness filter
    if (orderReadiness !== 'ALL') {
      otherFilters.push(
        orderReadiness === 'READY'
          ? 'Ready to order'
          : 'Needs more frames'
      );
    }

    return { selectedLocations, otherFilters };
  }, [filters, locations, manufacturers, orderReadiness]);

  const handleLocationAdd = (locationId: string) => {
    const currentLocationIds = filters.location_ids || [];
    const newLocationIds = [...currentLocationIds, locationId];

    // Save to localStorage
    localStorage.setItem('selectedLocationIds', JSON.stringify(newLocationIds));

    setFilters({
      ...filters,
      location_ids: newLocationIds,
    });
  };

  const handleLocationRemove = (locationId: string) => {
    let currentLocationIds = filters.location_ids || [];

    // If no filter is set (showing all locations), start with all location IDs
    if (currentLocationIds.length === 0 && locations) {
      currentLocationIds = locations.map((l) => l.id);
    }

    const newLocationIds = currentLocationIds.filter((id) => id !== locationId);

    // If no locations are selected, remove the filter entirely to show all locations
    if (newLocationIds.length === 0) {
      // Clear from localStorage
      localStorage.removeItem('selectedLocationIds');

      // eslint-disable-next-line @typescript-eslint/no-unused-vars
      const { location_ids, ...restFilters } = filters;
      setFilters(restFilters);
    } else {
      // Save to localStorage
      localStorage.setItem('selectedLocationIds', JSON.stringify(newLocationIds));

      setFilters({
        ...filters,
        location_ids: newLocationIds,
      });
    }
  };

  // Auto-select all frames when filtered manufacturer groups change
  useEffect(() => {
    if (filteredManufacturerGroups && filteredManufacturerGroups.length > 0) {
      const allFrameIds = filteredManufacturerGroups.flatMap((group) =>
        group.frames.map((f) => f.id)
      );
      setSelectedFrameIds(allFrameIds);
    } else {
      setSelectedFrameIds([]);
    }
  }, [filteredManufacturerGroups]);

  const handleFilterChange = (newFilters: FrameFilters) => {
    setFilters(newFilters);
    // Selection will be auto-updated by useEffect when data changes
  };

  const handleOrderSuccess = () => {
    // Refetch data to update the list
    refetch();
  };

  const toggleFilters = () => {
    const newValue = !showFilters;
    setShowFilters(newValue);
    localStorage.setItem('weeklyOrderingFiltersVisible', String(newValue));
  };

  // Loading state
  if (isLoading) {
    return (
      <div className="p-4 sm:p-6">
        <h1 className="text-3xl font-bold mb-6">Weekly Ordering</h1>
        <div className="bg-white rounded-lg shadow p-8">
          <div className="flex items-center justify-center">
            <div className="animate-spin rounded-full h-12 w-12 border-b-2 border-blue-600"></div>
            <span className="ml-4 text-gray-600">Loading pending frames...</span>
          </div>
        </div>
      </div>
    );
  }

  // Error state
  if (error) {
    return (
      <div className="p-4 sm:p-6">
        <h1 className="text-3xl font-bold mb-6">Weekly Ordering</h1>
        <div className="bg-white rounded-lg shadow p-8">
          <div className="text-center">
            <div className="text-red-600 text-5xl mb-4">⚠️</div>
            <h2 className="text-xl font-semibold text-gray-900 mb-2">
              Error Loading Data
            </h2>
            <p className="text-gray-600 mb-4">
              {error instanceof Error ? error.message : 'Failed to load pending frames'}
            </p>
            <button
              onClick={() => refetch()}
              className="px-4 py-2 bg-blue-600 text-white rounded-lg hover:bg-blue-700 transition-colors"
            >
              Try Again
            </button>
          </div>
        </div>
      </div>
    );
  }

  const totalFrames =
    filteredManufacturerGroups?.reduce((sum, group) => sum + group.frameCount, 0) || 0;
  const totalSelected = selectedFrameIds.length;

  return (
    <div className="p-6">
      {/* Header */}
      <div className="mb-6 flex flex-col sm:flex-row sm:justify-between sm:items-start gap-4">
        <div>
          <h1 className="text-2xl sm:text-3xl font-bold text-gray-900">Weekly Ordering</h1>
          <p className="text-gray-600 mt-1 text-sm sm:text-base">
            Review pending frames grouped by manufacturer and place batch orders
          </p>
        </div>
        <button
          onClick={toggleFilters}
          className="px-4 py-2 bg-white border border-gray-300 rounded-lg hover:bg-gray-50 transition-colors flex items-center gap-2 self-start sm:self-auto"
        >
          <svg
            className="w-5 h-5"
            fill="none"
            stroke="currentColor"
            viewBox="0 0 24 24"
          >
            <path
              strokeLinecap="round"
              strokeLinejoin="round"
              strokeWidth={2}
              d="M3 4a1 1 0 011-1h16a1 1 0 011 1v2.586a1 1 0 01-.293.707l-6.414 6.414a1 1 0 00-.293.707V17l-4 4v-6.586a1 1 0 00-.293-.707L3.293 7.293A1 1 0 013 6.586V4z"
            />
          </svg>
          {showFilters ? 'Hide Filters' : 'Show Filters'}
        </button>
      </div>

      {/* Active Filters Summary (shown when filters are hidden) */}
      {!showFilters && (
        <ActiveFiltersSummary
          selectedLocations={filterSummary.selectedLocations}
          availableLocations={locations?.map((l) => ({ id: l.id, name: l.name })) || []}
          otherFilters={filterSummary.otherFilters}
          onLocationAdd={handleLocationAdd}
          onLocationRemove={handleLocationRemove}
          onShowAllFilters={toggleFilters}
        />
      )}

      {/* Filters */}
      {showFilters && (
        <OrderingFilters
          filters={filters}
          onFilterChange={handleFilterChange}
          orderReadiness={orderReadiness}
          onOrderReadinessChange={setOrderReadiness}
        />
      )}

      {/* Stats Bar */}
      {totalFrames > 0 && (
        <div className="bg-blue-50 border border-blue-200 rounded-lg p-4 mb-6">
          <div className="flex items-center justify-between">
            <div className="flex items-center gap-6">
              <div>
                <span className="text-sm text-blue-700">Total Pending Frames:</span>
                <span className="ml-2 text-lg font-bold text-blue-900">{totalFrames}</span>
              </div>
              <div className="h-6 w-px bg-blue-300"></div>
              <div>
                <span className="text-sm text-blue-700">Manufacturers:</span>
                <span className="ml-2 text-lg font-bold text-blue-900">
                  {filteredManufacturerGroups?.length || 0}
                </span>
              </div>
              {totalSelected > 0 && (
                <>
                  <div className="h-6 w-px bg-blue-300"></div>
                  <div>
                    <span className="text-sm text-blue-700">Selected:</span>
                    <span className="ml-2 text-lg font-bold text-blue-900">{totalSelected}</span>
                  </div>
                </>
              )}
            </div>
            {totalSelected > 0 && (
              <button
                onClick={() => setSelectedFrameIds([])}
                className="text-sm text-blue-600 hover:text-blue-800 font-medium"
              >
                Clear Selection
              </button>
            )}
          </div>
        </div>
      )}

      {/* Empty State */}
      {(!filteredManufacturerGroups || filteredManufacturerGroups.length === 0) && (
        <div className="bg-white rounded-lg shadow p-12 text-center">
          <div className="text-6xl mb-4">✓</div>
          <h2 className="text-2xl font-semibold text-gray-900 mb-2">
            {orderReadiness === 'READY'
              ? 'No manufacturers ready to order'
              : orderReadiness === 'NEEDS_MORE'
                ? 'No manufacturers need more frames'
                : 'No frames to order. All caught up!'}
          </h2>
          <p className="text-gray-600 mb-6">
            {orderReadiness !== 'ALL'
              ? 'Try selecting a different readiness filter or adjusting other filters.'
              : filters.location_ids?.length ||
                  filters.manufacturer_ids?.length ||
                  filters.order_type ||
                  filters.date_from ||
                  filters.date_to
                ? 'Try adjusting your filters to see pending frames.'
                : 'All pending frames have been ordered. Check back later or add new frames.'}
          </p>
          {(filters.location_ids?.length ||
            filters.manufacturer_ids?.length ||
            filters.order_type ||
            filters.date_from ||
            filters.date_to ||
            orderReadiness !== 'ALL') && (
            <button
              onClick={() => {
                handleFilterChange({});
                setOrderReadiness('ALL');
              }}
              className="px-4 py-2 bg-blue-600 text-white rounded-lg hover:bg-blue-700 transition-colors"
            >
              Clear Filters
            </button>
          )}
        </div>
      )}

      {/* Manufacturer Groups */}
      {filteredManufacturerGroups && filteredManufacturerGroups.length > 0 && (
        <div className="space-y-6">
          {filteredManufacturerGroups.map((group) => (
            <ManufacturerGroup
              key={group.manufacturer.id}
              group={group}
              selectedFrameIds={selectedFrameIds}
              onSelectionChange={setSelectedFrameIds}
              onOrderSuccess={handleOrderSuccess}
            />
          ))}
        </div>
      )}
    </div>
  );
}
